import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { apiSlice } from '@/store/api/apiSlice';
import { sleep } from '@/utils';
import {
  parseDailyPrediction,
  parsePanchang,
  parseSpecialYogas,
  type DailyPanchang,
  type DailyPrediction,
  type RashiName,
  type SpecialYoga,
} from '@/utils/dailyPredictions';

export interface DailyPersonalPredictionArgs {
  userId: string;
  date: string;
}

export interface DailyGeneralPredictionArgs {
  date: string;
  rashi: RashiName;
  language: string;
}

// A failed general prediction request is retried 3 times, 2s apart, like the
// Weekly/Monthly tabs. The personalised one is not retried: the admin API answers
// at once with the prediction or a "not found" error. 401 is never retried: the
// base query is already logging out.
const GENERAL_RETRIES = 3;
const RETRY_DELAY_MS = 2000;

type BaseQueryResult = { data?: unknown; error?: FetchBaseQueryError };

async function fetchPrediction(
  run: () => BaseQueryResult | PromiseLike<BaseQueryResult>,
  retries = 0
): Promise<{ data: DailyPrediction } | { error: FetchBaseQueryError }> {
  for (let attempt = 0; ; attempt += 1) {
    const result = await run();
    if (!result.error) return { data: parseDailyPrediction(result.data) };
    if (attempt >= retries || result.error.status === 401) {
      return { error: result.error };
    }
    await sleep(RETRY_DELAY_MS);
  }
}

export const dailyPredictionApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getDailyPersonalPrediction: builder.query<DailyPrediction, DailyPersonalPredictionArgs>({
      queryFn: ({ userId, date }, _api, _extraOptions, baseQuery) =>
        fetchPrediction(() =>
          baseQuery({
            url: `/admin/users/${userId}/predictions/daily-personal`,
            method: 'GET',
            params: { date, language: 'English' },
          })
        ),
    }),
    getDailyGeneralPrediction: builder.query<DailyPrediction, DailyGeneralPredictionArgs>({
      queryFn: ({ date, rashi, language }, _api, _extraOptions, baseQuery) =>
        fetchPrediction(
          () =>
            baseQuery({
              url: '/daily-predictions/general',
              method: 'GET',
              params: { date, rashi, language },
            }),
          GENERAL_RETRIES
        ),
    }),
    getDailyPanchang: builder.query<DailyPanchang, string>({
      query: (date) => ({ url: `/panchang/${date}`, method: 'GET' }),
      transformResponse: (raw: unknown) => parsePanchang(raw),
    }),
    getDailyYogas: builder.query<SpecialYoga[], string>({
      query: (date) => ({ url: `/yoga/${date}`, method: 'GET' }),
      transformResponse: (raw: unknown) => parseSpecialYogas(raw),
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetDailyPersonalPredictionQuery,
  useGetDailyGeneralPredictionQuery,
  useGetDailyPanchangQuery,
  useGetDailyYogasQuery,
} = dailyPredictionApi;
